import React, { Component } from 'react';
import { compose } from 'redux';
import {
    TableBody as MuiTableBody,
    TableCell as MuiTableCell,
    TableRow as MuiTableRow,
    withStyles,
} from '@material-ui/core';
import type { Theme, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import DOMPurify from 'dompurify';

import { Block, Typography } from '@klp/design';
import { Amount } from '@klp/design/Amount';
import { DateTime } from '@klp/design/DateTime';
import { KlpStyles } from '@klp/design/Theme';
import { highlightSearch } from '@klp/search';

import type { ITableConsumerState } from './ITableConsumerState';
import type { TableColumnDef, TableColumnValue } from './TableColumnDef';
import { TableContext } from './TableContext';

const styles = (theme: Theme) => ({
    left: {
        textAlign: 'left',
    },
    center: {
        textAlign: 'center',
    },
    right: {
        textAlign: 'right',
    },
    tableCell: {
        padding: theme.spacing(1),
    },
    tableRow: {
        '&:nth-child(2n)': {
            backgroundColor: theme.palette.primary.veryLight,
        },
    },
    tableRowSelect: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
    },
});

export type TableBodyProps = WithStyles;

class TableBodyClass<DataType, FilterType> extends Component<TableBodyProps> {
    public static displayName = 'TableBody';

    private renderTableRow = (
        row: DataType,
        index: number,
        context: ITableConsumerState<DataType, FilterType>,
    ) => {
        const {
            renderTableCell,
            props: { classes },
        } = this;

        const rowClasses = classnames([classes.tableRow], {
            [classes.tableRowSelect]: !!context.onRowSelect,
        });
        return (
            <MuiTableRow
                key={index}
                data-testid="TableRow"
                onClick={() => {
                    if (context.onRowSelect) {
                        context.onRowSelect(row);
                    }
                }}
                className={rowClasses}
            >
                {context.columns.map(
                    (
                        column: TableColumnDef<DataType, FilterType>,
                        columnIndex: number,
                    ) => renderTableCell(row, column, context, columnIndex),
                )}
            </MuiTableRow>
        );
    };

    private renderTableCell = (
        row: DataType,
        column: TableColumnDef<DataType, FilterType>,
        context: ITableConsumerState<DataType, FilterType>,
        index: number,
    ) => (
        <MuiTableCell
            className={this.props.classes.tableCell}
            key={`${index}-${row[column.key]}`}
            data-testid="TableCell"
        >
            {this.renderDataType(row[column.key], column, context, row)}
        </MuiTableCell>
    );

    private renderDataType = (
        value: TableColumnValue,
        column: TableColumnDef<DataType, FilterType>,
        context: ITableConsumerState<DataType, FilterType>,
        row: DataType,
    ) => {
        const { classes } = this.props;
        const { highlightPhrase } = context;

        switch (column.type) {
            case 'amount':
                return (
                    <Typography
                        className={classes[column.align || 'center']}
                        klpTypographyVariant="small"
                    >
                        <Amount value={value as number} />
                    </Typography>
                );

            case 'date':
                return (
                    <Typography
                        className={classes[column.align || 'center']}
                        klpTypographyVariant="small"
                    >
                        <DateTime
                            date={value as Date}
                            format={DateTime.format.DayMonthYear}
                        />
                    </Typography>
                );

            case 'yearMonthDay':
                return (
                    <Typography
                        className={classes[column.align || 'center']}
                        klpTypographyVariant="small"
                    >
                        <DateTime date={value as Date} />
                    </Typography>
                );

            case 'dateTime':
                return (
                    <Typography
                        className={classes[column.align || 'center']}
                        klpTypographyVariant="small"
                    >
                        <DateTime
                            date={value as Date}
                            format={DateTime.format.DateTime}
                        />
                    </Typography>
                );

            default:
                return column.render ? (
                    <div className={classes[column.align || 'center']}>
                        {column.render(value, column, context, row)}
                    </div>
                ) : (
                    <Block>
                        {highlightPhrase && (
                            <Typography
                                className={classes[column.align || 'center']}
                                klpTypographyVariant="small"
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            highlightSearch(
                                                (value || '').toString(),
                                                highlightPhrase,
                                            ),
                                        ),
                                    }}
                                />
                            </Typography>
                        )}
                        {!highlightPhrase && (
                            <Typography
                                className={classes[column.align || 'center']}
                                klpTypographyVariant="small"
                            >
                                {value && value.toString()}
                            </Typography>
                        )}
                    </Block>
                );
        }
    };

    public render() {
        const { renderTableRow } = this;
        return (
            <TableContext.Consumer>
                {(context: ITableConsumerState<DataType, FilterType>) => (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                        {context.data && (
                            <MuiTableBody>
                                {context
                                    .paginate(context.data)
                                    .map((row, index) =>
                                        renderTableRow(row, index, context),
                                    )}
                            </MuiTableBody>
                        )}
                    </>
                )}
            </TableContext.Consumer>
        );
    }
}

const enhance = compose(withStyles(KlpStyles(styles)));

export const TableBody = enhance(TableBodyClass);
